/**
 * API service for tender document operations
 */

// Types for tender document API
export interface TenderDocumentData {
  id: string;
  title: string;
  description?: string;
  resource_type?: string;
  industry_sector?: string;
  score?: number;
  url?: string;
  summary?: string;
  company_id: string;
  created_at: string;
  updated_at: string;
  file_metadata?: {
    original_filename?: string;
    file_size?: number;
    file_type?: string;
    upload_method?: string;
  };
}

export interface TenderDocumentCreateRequest {
  title?: string;
  description: string;
  resource_type: string;
  industry: string;
  score?: number;
  urls?: string[];
}

export interface TenderDocumentListResponse {
  documents: TenderDocumentData[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface TenderDocumentStatsResponse {
  total_documents: number;
  overall_avg_score: number;
  by_resource_type: Array<{
    resource_type: string;
    count: number;
    avg_score: number;
  }>;
}

export interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// Base API URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ? 
  `${process.env.REACT_APP_API_BASE_URL}/api` :
  (process.env.NODE_ENV === 'production' 
    ? 'https://api-beta-open-tender.nat-quest.com/api' 
    : 'http://localhost:2024/api');

// Get auth token from localStorage (aligned with projectApi.ts)
const getAuthToken = (): string | null => {
  try {
    const stored = localStorage.getItem('opentender_tokens');
    if (!stored) return null;
    
    const { accessToken } = JSON.parse(stored);
    return accessToken;
  } catch {
    // Fallback to old method
    return localStorage.getItem('access_token');
  }
};

// Create headers with authentication
const createHeaders = (includeContentType = true): HeadersInit => {
  const headers: HeadersInit = {};
  
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  
  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  return headers;
};

// Handle API responses
const handleResponse = async <T>(response: Response): Promise<APIResponse<T>> => {
  if (response.ok) {
    try {
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to parse response' };
    }
  } else {
    try {
      const errorData = await response.json();
      return { 
        success: false, 
        error: errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}` 
      };
    } catch (error) {
      return { 
        success: false, 
        error: `HTTP ${response.status}: ${response.statusText}` 
      };
    }
  }
};

export class TenderDocumentApi {
  /**
   * Create a new tender document
   */
  static async createTenderDocument(request: TenderDocumentCreateRequest): Promise<APIResponse<TenderDocumentData>> {
    try {
      // Since we're now using Cloudinary URLs instead of direct file uploads,
      // we can use JSON instead of FormData
      const requestBody = {
        title: request.title,
        description: request.description,
        resource_type: request.resource_type,
        industry: request.industry,
        score: request.score,
        urls: request.urls
      };
      
      const response = await fetch(`${API_BASE_URL}/tender-documents`, {
        method: 'POST',
        headers: createHeaders(true), // Include Content-Type for JSON
        body: JSON.stringify(requestBody),
      });
      
      return handleResponse<TenderDocumentData>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }

  /**
   * Get list of tender documents
   */
  static async getTenderDocuments(
    page = 1,
    pageSize = 20,
    resourceTypeFilter?: string,
    industryFilter?: string,
    search?: string
  ): Promise<APIResponse<TenderDocumentListResponse>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });
      
      if (resourceTypeFilter) {
        params.append('resource_type_filter', resourceTypeFilter);
      }
      
      if (industryFilter) {
        params.append('industry_filter', industryFilter);
      }
      
      if (search) {
        params.append('search', search);
      }
      
      const response = await fetch(`${API_BASE_URL}/tender-documents?${params}`, {
        method: 'GET',
        headers: createHeaders(),
      });
      
      return handleResponse<TenderDocumentListResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }

  /**
   * Get a specific tender document by ID
   */
  static async getTenderDocument(documentId: string): Promise<APIResponse<TenderDocumentData>> {
    try {
      const response = await fetch(`${API_BASE_URL}/tender-documents/${documentId}`, {
        method: 'GET',
        headers: createHeaders(),
      });
      
      return handleResponse<TenderDocumentData>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }

  /**
   * Update a tender document
   */
  static async updateTenderDocument(
    documentId: string,
    updates: Partial<TenderDocumentCreateRequest>
  ): Promise<APIResponse<TenderDocumentData>> {
    try {
      const response = await fetch(`${API_BASE_URL}/tender-documents/${documentId}`, {
        method: 'PUT',
        headers: createHeaders(),
        body: JSON.stringify(updates),
      });
      
      return handleResponse<TenderDocumentData>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }

  /**
   * Delete a tender document
   */
  static async deleteTenderDocument(documentId: string): Promise<APIResponse<{ message: string }>> {
    try {
      const response = await fetch(`${API_BASE_URL}/tender-documents/${documentId}`, {
        method: 'DELETE',
        headers: createHeaders(),
      });
      
      return handleResponse<{ message: string }>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }

  /**
   * Get tender document statistics
   */
  static async getTenderDocumentStats(): Promise<APIResponse<TenderDocumentStatsResponse>> {
    try {
      const response = await fetch(`${API_BASE_URL}/tender-documents/stats`, {
        method: 'GET',
        headers: createHeaders(),
      });
      
      return handleResponse<TenderDocumentStatsResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }
}